<script setup lang="ts">
/**
 * Inactivity re-engagement nudge (PE-8.6).
 *
 * The idle signal behind this toast is **on-device behavioural only** —
 * elapsed seconds without a keystroke, click or submit on the displayed
 * question. There are no sensors, no biometrics, no eye-tracking and no
 * webcam: attention detection of that kind is permanently ruled out for an
 * under-18 cohort (UK-GDPR child data), not merely unimplemented. Do not
 * "improve" this with a camera. Only the aggregate `idle_seconds` count ever
 * leaves the device, through the analytics payload guard.
 *
 * It is a toast, not a modal: no backdrop, no focus trap, and it never steals
 * focus from the answer textarea mid-sentence. The wrapper is
 * `pointer-events: none` so even when it sits over content, clicks and caret
 * placement pass straight through to the panel underneath; only the dismiss
 * button itself is interactive. Copy is a question, not a reprimand.
 *
 * The container owns the idle timer and hides this on any activity; the
 * `dismiss` emit is only the explicit button press.
 */

defineProps<{ visible: boolean }>();

defineEmits<{ dismiss: [] }>();
</script>

<template>
  <!-- The live region is always mounted so the insertion of the toast is what
       gets announced, politely — a screen-reader user hears it at the next
       pause rather than being interrupted. -->
  <div class="nudge-region" role="status" aria-live="polite">
    <div v-if="visible" class="nudge rl-anim-toast-in">
      <span class="nudge-text">Still with us? Pick up where you left off.</span>
      <button type="button" class="nudge-dismiss" @click="$emit('dismiss')">
        Resume
      </button>
    </div>
  </div>
</template>

<style scoped>
/* The region anchors the left edge at the viewport centre; the toast itself
   pulls back by half its own width, matching the rl-toast-in keyframes so the
   position is identical during and after the animation. */
.nudge-region {
  position: fixed;
  bottom: var(--sp-6);
  left: 50%;
  z-index: 3;
  /* Never intercept input aimed at the answer textarea below it. */
  pointer-events: none;
}

.nudge {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--radius);
  background: var(--rl-fg-1);
  color: var(--rl-fg-invert);
  box-shadow: var(--shadow-lg);
  white-space: nowrap;
  /* Under prefers-reduced-motion the global override swaps in an opacity-only
     entrance that carries no transform, so the static one has to stand alone. */
  transform: translateX(-50%);
}

.nudge-text {
  font-size: var(--rl-text-base);
  font-weight: 500;
}

.nudge-dismiss {
  flex: none;
  padding: 5px 12px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--rl-neutral-25);
  color: var(--rl-fg-1);
  font-size: var(--rl-text-base);
  font-weight: 600;
  cursor: pointer;
  /* Re-enabled only on the affordance: the toast stays click-through. */
  pointer-events: auto;
}

.nudge-dismiss:hover {
  background: var(--rl-bg-subtle);
}
</style>
